import { makeAutoObservable, runInAction } from 'mobx'
import agent, {
  AdminUserDto,
  AdminUserDetailDto,
  AdminUserParams,
  EditUserValues
} from '../api/agent'
import { PagedResult } from '../models/pagedResult'
import type {
  OrphanedContentDto,
  OrphanedContentParams,
  AdminActionLogDto,
  BulkReassignResult,
  InternalTagSummaryDto,
  InternalTagContentDto,
  PaginationParams
} from '../models/admin'

export function orphanedItemId (row: OrphanedContentDto): string {
  return `${row.contentType}|${row.id}`
}

export default class AdminStore {
  users: PagedResult<AdminUserDto> | null = null
  selectedUser: AdminUserDetailDto | null = null
  loading = false
  loadingDetail = false
  submitting = false

  orphanedContent: PagedResult<OrphanedContentDto> | null = null
  loadingOrphaned = false
  selectedOrphanedItems: Set<string> = new Set()
  selectedOrphanedDetails: Map<string, { title: string; contentType: string }> = new Map()
  reassignmentLog: PagedResult<AdminActionLogDto> | null = null
  loadingReassignmentLog = false
  lastBulkReassignResult: BulkReassignResult | null = null

  internalTags: InternalTagSummaryDto[] = []
  internalTagContent: PagedResult<InternalTagContentDto> | null = null
  loadingInternalTags = false
  selectedInternalTag: string | null = null

  constructor () {
    makeAutoObservable(this)
  }

  loadUsers = async (params: AdminUserParams = {}) => {
    this.loading = true
    try {
      const result = await agent.Admin.listUsers({
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20,
        q: params.q,
        tier: params.tier,
        isSuspended: params.isSuspended,
        isAdmin: params.isAdmin
      })
      runInAction(() => {
        this.users = result
        this.loading = false
      })
    } catch (error) {
      runInAction(() => (this.loading = false))
      throw error
    }
  }

  loadUser = async (username: string) => {
    this.loadingDetail = true
    this.selectedUser = null
    try {
      const user = await agent.Admin.getUser(username)
      runInAction(() => {
        this.selectedUser = user
        this.loadingDetail = false
      })
    } catch (error) {
      runInAction(() => (this.loadingDetail = false))
      throw error
    }
  }

  editUser = async (username: string, values: EditUserValues) => {
    this.submitting = true
    try {
      const updated = await agent.Admin.editUser(username, { ...values, username })
      runInAction(() => {
        this.selectedUser = updated ? { ...this.selectedUser!, ...updated } : this.selectedUser
        if (this.users?.items) {
          const idx = this.users.items.findIndex((u) => u.userName === username)
          if (idx >= 0) this.users.items[idx] = { ...this.users.items[idx], ...updated }
        }
        this.submitting = false
      })
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }

  adjustReputation = async (username: string, delta: number, detail: string, isVisibleToUser = false) => {
    this.submitting = true
    try {
      const updated = await agent.Admin.adjustReputation(username, { username, delta, detail, isVisibleToUser })
      runInAction(() => {
        if (this.selectedUser && this.selectedUser.userName === username) {
          this.selectedUser = { ...this.selectedUser, ammoScore: updated.ammoScore, ammoTier: updated.ammoTier }
        }
        if (this.users?.items) {
          const idx = this.users.items.findIndex((u) => u.userName === username)
          if (idx >= 0) {
            this.users.items[idx] = { ...this.users.items[idx], ammoScore: updated.ammoScore, ammoTier: updated.ammoTier }
          }
        }
        this.submitting = false
      })
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }

  deleteUser = async (username: string) => {
    this.submitting = true
    try {
      await agent.Admin.deleteUser(username)
      runInAction(() => {
        this.selectedUser = null
        if (this.users?.items) {
          this.users.items = this.users.items.filter((u) => u.userName !== username)
          this.users.totalCount = Math.max(0, (this.users.totalCount ?? 1) - 1)
        }
        this.submitting = false
      })
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }

  resetPassword = async (
    username: string,
    body: { newPassword: string; confirmPassword: string }
  ) => {
    this.submitting = true
    try {
      await agent.Admin.resetPassword(username, body)
      const user = await agent.Admin.getUser(username)
      runInAction(() => {
        this.selectedUser = user
        this.submitting = false
      })
    } catch (error) {
      runInAction(() => (this.submitting = false))
      throw error
    }
  }

  loadOrphanedContent = async (params: OrphanedContentParams = {}) => {
    this.loadingOrphaned = true
    try {
      const result = await agent.Admin.getOrphanedContent({
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 25,
        contentType: params.contentType
      })
      runInAction(() => {
        this.orphanedContent = result
        this.loadingOrphaned = false
      })
    } catch (error) {
      runInAction(() => (this.loadingOrphaned = false))
      throw error
    }
  }

  toggleOrphanSelection = (id: string, row?: OrphanedContentDto) => {
    const next = new Set(this.selectedOrphanedItems)
    const details = new Map(this.selectedOrphanedDetails)
    if (next.has(id)) {
      next.delete(id)
      details.delete(id)
    } else {
      next.add(id)
      if (row) details.set(id, { title: row.title, contentType: row.contentType })
    }
    this.selectedOrphanedItems = next
    this.selectedOrphanedDetails = details
  }

  selectAllOrphans = () => {
    if (!this.orphanedContent?.items?.length) return
    const next = new Set(this.selectedOrphanedItems)
    const details = new Map(this.selectedOrphanedDetails)
    this.orphanedContent.items.forEach((row) => {
      const id = orphanedItemId(row)
      next.add(id)
      details.set(id, { title: row.title, contentType: row.contentType })
    })
    this.selectedOrphanedItems = next
    this.selectedOrphanedDetails = details
  }

  clearSelection = () => {
    this.selectedOrphanedItems = new Set()
    this.selectedOrphanedDetails = new Map()
    this.lastBulkReassignResult = null
  }

  reassignContent = async (
    contentType: string,
    contentId: string,
    newOwnerUsername: string,
    reason: string
  ) => {
    await agent.Admin.reassignContent({
      contentType,
      contentId,
      newOwnerUsername,
      reason
    })
    runInAction(() => {
      if (this.orphanedContent?.items) {
        this.orphanedContent = {
          ...this.orphanedContent,
          items: this.orphanedContent.items.filter(
            (r) => orphanedItemId(r) !== `${contentType}|${contentId}`
          ),
          totalCount: Math.max(0, (this.orphanedContent.totalCount ?? 1) - 1)
        }
      }
      const id = `${contentType}|${contentId}`
      const next = new Set(this.selectedOrphanedItems)
      next.delete(id)
      this.selectedOrphanedItems = next
      const details = new Map(this.selectedOrphanedDetails)
      details.delete(id)
      this.selectedOrphanedDetails = details
    })
  }

  bulkReassign = async (newOwnerUsername: string, reason: string) => {
    const items = Array.from(this.selectedOrphanedItems).map((id) => {
      const [contentType, contentId] = id.split('|')
      return { contentType, contentId }
    })
    const result = await agent.Admin.bulkReassign({
      items,
      newOwnerUsername,
      reason
    })
    runInAction(() => {
      this.lastBulkReassignResult = result
      this.selectedOrphanedItems = new Set()
      this.selectedOrphanedDetails = new Map()
    })
    return result
  }

  loadReassignmentLog = async (params: { pageNumber?: number; pageSize?: number } = {}) => {
    this.loadingReassignmentLog = true
    try {
      const result = await agent.Admin.getReassignmentLog({
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 20
      })
      runInAction(() => {
        this.reassignmentLog = result
        this.loadingReassignmentLog = false
      })
    } catch (error) {
      runInAction(() => (this.loadingReassignmentLog = false))
      throw error
    }
  }

  loadInternalTags = async () => {
    this.loadingInternalTags = true
    try {
      const list = await agent.Admin.listInternalTags()
      runInAction(() => {
        this.internalTags = list ?? []
        this.loadingInternalTags = false
      })
    } catch (error) {
      runInAction(() => (this.loadingInternalTags = false))
      throw error
    }
  }

  loadInternalTagContent = async (tagName: string, params: PaginationParams = {}) => {
    this.selectedInternalTag = tagName
    try {
      const result = await agent.Admin.getInternalTagContent(tagName, {
        pageNumber: params.pageNumber ?? 1,
        pageSize: params.pageSize ?? 25
      })
      runInAction(() => {
        this.internalTagContent = result
      })
    } catch (error) {
      runInAction(() => {
        this.internalTagContent = null
      })
      throw error
    }
  }

  applyInternalTag = async (tagName: string, targetType: number, targetId: string) => {
    return await agent.Admin.applyInternalTag({ tagName, targetType, targetId })
  }

  removeInternalTag = async (entityTagId: string) => {
    await agent.Admin.removeInternalTag(entityTagId)
    runInAction(() => {
      if (this.internalTagContent?.items) {
        this.internalTagContent = {
          ...this.internalTagContent,
          items: this.internalTagContent.items.filter((item) => item.entityTagId !== entityTagId),
          totalCount: Math.max(0, (this.internalTagContent.totalCount ?? 1) - 1)
        }
      }
    })
  }
}
